import { accountRisksSettingsEdit } from "@/redux/account.actions"
import Card from "@/components/card"
import Link from "@/components/link"
import Space from "@/components/space"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faScaleBalanced, faUndo } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import RiskScaleSection from "@/entities/physical-risk/ui/RiskScaleSection"
import { PhysicalRiskScaleItem } from "@/entities/physical-risk/model/physical-risk.types"
import {
  addResidualScaleItem,
  addScaleItem,
  editScaleItem,
  getResidualScaleItems,
  getResidualSettings,
  removeScaleItem,
  ResidualScaleItem,
  sortScaleItems
} from "@/entities/physical-risk/utils/sortScales"
import { createEmptyAccountRisksSettings, DEFAULT_SCALES, getResidualRiskLevels } from "@/redux/account.types"
import Checkbox from "@/components/checkbox"

export type EditableScaleKey = "severityScale" | "frequencyScale" | "preventionScale"

type Props = {
  onDirty?: () => void
}

export default function RiskScalesConfig({ onDirty }: Props) {
  const dispatch = useAppDispatch()

  const draft = useAppSelector((state) => state._session.accountRisksSettings)

  const getCurrentItems = (scaleKey: EditableScaleKey): PhysicalRiskScaleItem[] =>
    draft[scaleKey] ?? DEFAULT_SCALES[scaleKey]

  const handleReset = () => {
    for (const scaleKey of ["severityScale", "frequencyScale", "preventionScale"] as const) {
      dispatch(accountRisksSettingsEdit({ key: scaleKey, value: sortScaleItems([...DEFAULT_SCALES[scaleKey]]) }))
    }
    dispatch(
      accountRisksSettingsEdit({
        key: "residualRiskScale",
        value: createEmptyAccountRisksSettings().residualRiskScale
      })
    )
    dispatch(accountRisksSettingsEdit({ key: "residualRiskLevels", value: getResidualRiskLevels() }))

    onDirty?.()
  }

  const updateScale = (
    scaleKey: EditableScaleKey,
    updater: (items: PhysicalRiskScaleItem[]) => PhysicalRiskScaleItem[]
  ) => {
    dispatch(
      accountRisksSettingsEdit({ key: scaleKey, value: sortScaleItems(updater([...getCurrentItems(scaleKey)])) })
    )
    onDirty?.()
  }

  const handleEditItem = (scaleKey: EditableScaleKey, index: number, patch: Partial<PhysicalRiskScaleItem>) => {
    updateScale(scaleKey, (items) => editScaleItem(items, index, patch))
  }

  const handleAddItem = (scaleKey: EditableScaleKey, index: number) => {
    updateScale(scaleKey, (items) => addScaleItem(items, index))
  }

  const handleRemoveItem = (scaleKey: EditableScaleKey, index: number) => {
    updateScale(scaleKey, (items) => removeScaleItem(items, index))
  }

  const residualItems = getResidualScaleItems(draft)

  //Pas de tri a la frappe : le seuil est pris tel que tape (le borner empechait d'ecrire "90")
  const updateResidual = (updater: (items: ResidualScaleItem[]) => ResidualScaleItem[]) => {
    const { residualRiskScale, residualRiskLevels } = getResidualSettings(updater(residualItems))
    dispatch(accountRisksSettingsEdit({ key: "residualRiskScale", value: residualRiskScale }))
    dispatch(accountRisksSettingsEdit({ key: "residualRiskLevels", value: residualRiskLevels }))
    onDirty?.()
  }

  return (
    <Card className="space-y-6">
      <div className="mb-4 flex items-center gap-3">
        <FontAwesomeIcon icon={faScaleBalanced} className="grey-t text-lg" />
        <span className="text-lg">{t("Configuration des barèmes de risques")}</span>
        <Space />
        <Link icon={faUndo} isWithoutMargin onClick={handleReset}>
          {t("Réinitialiser les valeurs par défaut")}
        </Link>
      </div>

      <RiskScaleSection
        title={t("risk_item_s")}
        scaleKey="severityScale"
        items={getCurrentItems("severityScale")}
        onEditItem={handleEditItem}
        onAddItem={handleAddItem}
        onRemoveItem={handleRemoveItem}
      />

      <RiskScaleSection
        title={t("risk_item_f")}
        scaleKey="frequencyScale"
        items={getCurrentItems("frequencyScale")}
        onEditItem={handleEditItem}
        onAddItem={handleAddItem}
        onRemoveItem={handleRemoveItem}
      />

      <RiskScaleSection
        title={t("risk_item_p")}
        scaleKey="preventionScale"
        items={getCurrentItems("preventionScale")}
        onEditItem={handleEditItem}
        onAddItem={handleAddItem}
        onRemoveItem={handleRemoveItem}
      />

      <RiskScaleSection
        title={t("risk_item_rr")}
        scaleKey="residualRiskScale"
        items={residualItems}
        isFirstValueFixed
        isDescriptionHidden
        withColor
        onEditItem={(_, index, patch) =>
          updateResidual((items) => editScaleItem(items, index, patch as Partial<ResidualScaleItem>))
        }
        onAddItem={(_, index) => updateResidual((items) => addResidualScaleItem(items, index))}
        onRemoveItem={(_, index) => updateResidual((items) => removeScaleItem(items, index))}
      />

      <Checkbox
        active={!draft.isIdtreeScoreHidden}
        text={t("Afficher la note IDTree")}
        onClick={() => {
          dispatch(accountRisksSettingsEdit({ key: "isIdtreeScoreHidden", value: !draft.isIdtreeScoreHidden }))
          onDirty?.()
        }}
      />
    </Card>
  )
}
